import Big from 'big.js'
import { Measure } from '../types'


enum TemperatureMeasurementSystem {
  siUnits = 'SI Units',
  commonUsage = 'Common Units',
  scientificAndFixedPoints = 'Scientific and Fixed Points Units',
  historical = 'Historical Units'
}

const temperatureUnits = {
  celsius: {
    displayName: 'Celsius',
    codeName: 'celsius',
    url: 'celsius',
    symbol: '°C',
    toBaseUnit: (value: Big) => value.plus(273.15),
    fromBaseUnit: (value: Big) => value.minus(273.15),
    system: TemperatureMeasurementSystem.commonUsage
  },
  fahrenheit: {
    displayName: 'Fahrenheit',
    codeName: 'fahrenheit',
    url: 'fahrenheit',
    symbol: '°F',
    toBaseUnit: (value: Big) => value.plus(459.67).times(5).div(9),
    fromBaseUnit: (value: Big) => value.times(9).div(5).minus(459.67),
    system: TemperatureMeasurementSystem.commonUsage
  },

  kelvin: {
    displayName: 'Kelvin',
    codeName: 'kelvin',
    url: 'kelvin',
    symbol: 'K',
    toBaseUnit: (value: Big) => value,
    fromBaseUnit: (value: Big) => value,
    system: TemperatureMeasurementSystem.siUnits
  },

  triple_point_of_water: {
    displayName: 'Triple point of water',
    codeName: 'triple_point_of_water',
    url: 'triple-point-of-water',
    symbol: 'TPW',
    toBaseUnit: (value: Big) => value.div(0.0036608581),
    fromBaseUnit: (value: Big) => value.times(0.0036608581),
    system: TemperatureMeasurementSystem.scientificAndFixedPoints
  },

  newton: {
    displayName: 'Newton',
    codeName: 'newton',
    url: 'newton',
    symbol: 'N',
    toBaseUnit: (value: Big) => value.times(100 / 33).plus(273.15),
    fromBaseUnit: (value: Big) => value.minus(273.15).times(33 / 100),
    system: TemperatureMeasurementSystem.historical
  },
  romer: {
    displayName: 'Romer',
    codeName: 'romer',
    url: 'romer',
    symbol: '°Rø',
    toBaseUnit: (value: Big) => value.minus(7.5).times(40 / 21).plus(273.15),
    fromBaseUnit: (value: Big) => value.minus(273.15).times(21 / 40).plus(7.5),
    system: TemperatureMeasurementSystem.historical
  },
  reaumur: {
    displayName: 'Reaumur',
    codeName: 'reaumur',
    url: 'reaumur',
    symbol: '°Ré',
    toBaseUnit: (value: Big) => value.times(5 / 4).plus(273.15),
    fromBaseUnit: (value: Big) => value.minus(273.15).times(4 / 5),
    system: TemperatureMeasurementSystem.historical
  },
  delisle: {
    displayName: 'Delisle',
    codeName: 'delisle',
    url: 'delisle',
    symbol: '°De',
    toBaseUnit: (value: Big) => value.times(-2 / 3).plus(373.15),
    fromBaseUnit: (value: Big) => value.minus(373.15).times(-3 / 2),
    system: TemperatureMeasurementSystem.historical
  },
  rankine: {
    displayName: 'Rankine',
    codeName: 'rankine',
    url: 'rankine',
    symbol: '°R',
    toBaseUnit: (value: Big) => value.div(1.8),
    fromBaseUnit: (value: Big) => value.times(1.8),
    system: TemperatureMeasurementSystem.historical
  }
}

export const temperature: Measure = {
  displayName: 'Temperature',
  codeName: 'temperature',
  url: 'temperature',
  units: temperatureUnits,
  baseUnit: temperatureUnits.kelvin, // just for reference
  defaultFromUnit: temperatureUnits.celsius,
  defaultToUnit: temperatureUnits.fahrenheit
}
